/* Capability-based permissions. The admin edits a role×capability matrix in the
   Team page; that same matrix is the enforcement (can() reads it everywhere).
   The overall admin (the studio principal, HDSTUDIO) is always full-access. */

export type Capability =
  | "brief" | "presentation" | "stages" | "reports" | "estimator"
  | "payments" | "feedback" | "projects" | "contracts" | "reviews" | "team";

export const CAPABILITIES: { key: Capability; label: string }[] = [
  { key: "brief", label: "Design brief" },
  { key: "presentation", label: "Presentations" },
  { key: "reviews", label: "Drawing reviews" },
  { key: "stages", label: "Stages" },
  { key: "reports", label: "Reports" },
  { key: "estimator", label: "Fee estimator" },
  { key: "payments", label: "Payments" },
  { key: "feedback", label: "Feedback" },
  { key: "projects", label: "New projects" },
  { key: "contracts", label: "Contracts" },
  { key: "team", label: "Team & roles" },
];

/** Which capability opens each cockpit section. A section listed here is
 *  unreachable without its capability — the table is access, not just edit
 *  rights, so switching one off actually shuts the door.
 *  `null` = any signed-in team member (the dashboard is everyone's landing).
 *  `projects` and `contracts` aren't here: they gate actions (create a project,
 *  hand one to the Contract Generator), not a section of their own. */
export const SECTION_CAP: Record<string, Capability | null> = {
  dashboard: null,
  data: "brief",
  brief: "brief",
  moodboard: "brief",
  concept: "presentation",
  presentation: "presentation",
  review: "reviews",
  tendering: "reviews",
  drawings: "reviews",
  rfis: "reviews",
  site: "stages",
  snags: "stages",
  costplan: "estimator",
  costbrief: "estimator",
  audit: null,
  stages: "stages",
  schedule: "stages",
  program: "stages",
  reports: "reports",
  estimator: "estimator",
  quotes: "payments",
  billing: "payments",
  ledger: "payments",
  engagement: "contracts",
  feedback: "feedback",
  team: "team",
  rates: "team",
};

export type RoleKey = "admin" | "assistant" | "lead" | "designer" | "accountant" | "viewer";

export const ROLES: { key: RoleKey; label: string; blurb: string; locked?: boolean }[] = [
  { key: "admin",     label: "Admin", blurb: "Full control, including team & roles.", locked: true },
  { key: "assistant", label: "Admin assistant", blurb: "Delegated near-full access.", },
  { key: "lead",      label: "Studio lead", blurb: "Runs projects day to day." },
  { key: "designer",  label: "Designer", blurb: "Works on briefs & design." },
  { key: "accountant", label: "Accountant", blurb: "Billing, invoices and payments — no design access." },
  { key: "viewer",    label: "Viewer", blurb: "Read-only." },
];

export type RoleMatrix = Record<RoleKey, Record<Capability, boolean>>;

function row(caps: Capability[]): Record<Capability, boolean> {
  const r = {} as Record<Capability, boolean>;
  for (const c of CAPABILITIES) r[c.key] = caps.includes(c.key);
  return r;
}

export const ALL_CAPS: Capability[] = CAPABILITIES.map((c) => c.key);

export const DEFAULT_MATRIX: RoleMatrix = {
  admin: row(ALL_CAPS),
  assistant: row(["brief", "presentation", "reviews", "stages", "reports", "estimator", "payments", "feedback", "projects", "contracts"]),
  lead: row(["brief", "presentation", "reviews", "stages", "reports", "estimator", "feedback", "contracts"]),
  designer: row(["brief", "presentation", "reviews", "estimator", "feedback"]),
  // the money, and what it is for — nothing that edits the design
  accountant: row(["payments", "reports"]),
  viewer: row([]),
};

/** Merge a stored matrix over the defaults so new capabilities/roles never break. */
export function normalizeMatrix(stored?: Partial<RoleMatrix>): RoleMatrix {
  const out = {} as RoleMatrix;
  for (const r of ROLES) {
    const base = DEFAULT_MATRIX[r.key];
    const saved = stored?.[r.key] || {};
    out[r.key] = r.locked ? row(ALL_CAPS) : { ...base, ...saved } as Record<Capability, boolean>;
  }
  return out;
}
